import { HttpClient } from '@angular/common/http';
import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { mapResponse } from 'Core/helpers';
import { LoginRequest } from 'Core/interfaces';
import { AuthResponse } from 'Core/interfaces/auth-response.interface';
import { User, UserRegistrationInfo, UserView } from 'Core/models';
import { isNil } from 'lodash';
import { NgxPermissionsService } from 'ngx-permissions';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { SocketEventHandlerService } from './socket-event-handler.service';
import { SocketService } from './socket.service';
import { CurrentUserStateService } from './state';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private storageKey = 'accessToken';
  private refreshTokenKey = 'refreshToken';

  public readonly accessToken: WritableSignal<string | null> = signal(localStorage.getItem(this.storageKey));

  public readonly refreshToken: WritableSignal<string | null> = signal(localStorage.getItem(this.refreshTokenKey));

  public readonly hasToken: Signal<boolean> = computed(() => !isNil(this.accessToken()));
  private readonly baseUrl: string = '/auth';
  private refreshInProgress$: Observable<{ accessToken: string }> | null = null;
  private socketEventsInitialized = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly router: Router,
    private readonly currentUserStateService: CurrentUserStateService,
    private readonly socketService: SocketService,
    private readonly socketEventHandler: SocketEventHandlerService,
    private readonly ngxPermissionsService: NgxPermissionsService
  ) {
    const token = this.accessToken();
    if (token) {
      this.connectSocket(token);
    }
  }

  public updateRefreshToken(): Observable<{ accessToken: string }> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const refreshTokenValue = this.refreshToken();
    if (isNil(refreshTokenValue)) {
      this.performLogout();
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshInProgress$ = this.httpClient
      .post<Omit<AuthResponse, 'user'>>(`${this.baseUrl}/refresh-token`, { refreshToken: refreshTokenValue })
      .pipe(
        catchError(error => {
          this.refreshInProgress$ = null;
          if (error.status === 401) {
            this.performLogout();
          }
          return throwError(() => error);
        }),
        tap(({ accessToken, refreshToken }) => {
          this.saveToken(refreshToken, accessToken);
          this.socketService.reconnect(accessToken);
        }),
        map(res => {
          this.refreshInProgress$ = null;
          return { accessToken: res.accessToken };
        }),
        shareReplay(1)
      );

    return this.refreshInProgress$;
  }

  public login(body: LoginRequest) {
    return this.httpClient.post<AuthResponse>(`${this.baseUrl}/login`, body).pipe(
      tap(res => {
        this.accessToken.set(res.accessToken);
        this.refreshToken.set(res.refreshToken);
        localStorage.setItem(this.storageKey, res.accessToken);
        localStorage.setItem(this.refreshTokenKey, res.refreshToken);

        // Юзер и права должны попасть в стор до навигации: canActivate-гарды
        // отрабатывают раньше UserResolver, и без прав ngxPermissionsGuard
        // уводит на редирект, замыкая цикл app <-> app/dashboard.
        const user = new User(res.user);
        this.currentUserStateService.setUser(user);
        this.ngxPermissionsService.loadPermissions(user.collectionRoles);

        this.connectSocket(res.accessToken);
      })
    );
  }

  public register(body: UserRegistrationInfo) {
    return this.httpClient.post<UserView>(`${this.baseUrl}/registration`, body).pipe(mapResponse(User));
  }

  public logout(): Observable<boolean> {
    this.performLogout();
    return from(this.router.navigateByUrl('/welcome-page'));
  }

  private performLogout(): void {
    this.socketService.disconnect();
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.refreshTokenKey);
    this.accessToken.set(null);
    this.refreshToken.set(null);
    this.currentUserStateService.resetState();
    this.ngxPermissionsService.flushPermissions();
    this.router.navigateByUrl('/welcome-page');
  }

  private connectSocket(token: string): void {
    this.socketService.connect(token);

    if (!this.socketEventsInitialized) {
      this.socketEventHandler.init();
      this.socketEventsInitialized = true;
    }
  }

  private saveToken(refreshToken: string, accessToken: string): void {
    localStorage.setItem(this.storageKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    this.accessToken.set(accessToken);
    this.refreshToken.set(refreshToken);
  }
}
